import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { toggleKitStatus, updateKit, updateKitBulk } from '../config/api';

// Kit × hub grid for the Custom Kits page (desktop).
// One row per kit batch (all hub copies of one kit share a kit_batch_id), one column per hub.
// A cell is that kit at that hub: live, switched off, not added, or different from the other hubs.

const ALL_HUBS_KEY = 'all';

const groupKeyOf = (kit) => kit.kit_batch_id || `kit-${kit.id}`;
const hubKeyOf = (kit) => (kit.warehouse_id ? String(kit.warehouse_id) : ALL_HUBS_KEY);
const categoryKeyOf = (cat) => `${cat.category_id}|${cat.label || ''}`;
const categoryNameOf = (cat) => cat.label || cat.category_name || `Category ${cat.category_id}`;
const skuNameOf = (sku) => (sku.product && (sku.product.skuCode || sku.product.name)) || `#${sku.product_id}`;

// What must match across hubs for a kit to count as "the same"
const signatureOf = (kit) => JSON.stringify({
  name: kit.name,
  discount: Number(kit.discount_percent) || 0,
  gst: Number(kit.gst_percent) || 0,
  hsn: kit.hsn_code || '',
  minOptional: kit.min_optional_count || 0,
  categories: (kit.categories || []).map((c) => ({
    key: categoryKeyOf(c),
    mandatory: !!c.is_mandatory,
    min: c.min_qty,
    max: c.max_qty,
    skus: (c.skus || []).map((s) => s.product_id).sort((a, b) => a - b),
  })).sort((a, b) => a.key.localeCompare(b.key)),
});

// Human-readable differences of one hub copy against the reference copy
const diffKit = (kit, ref) => {
  const out = [];
  const field = (label, a, b, unit = '') => {
    if (String(a ?? '') !== String(b ?? '')) out.push({ type: 'change', text: `${label} ${a ?? '-'}${unit} (other hubs ${b ?? '-'}${unit})` });
  };
  if (kit.name !== ref.name) out.push({ type: 'change', text: `Name "${kit.name}" (other hubs "${ref.name}")` });
  field('Discount', Number(kit.discount_percent) || 0, Number(ref.discount_percent) || 0, '%');
  field('GST', kit.gst_percent, ref.gst_percent, '%');
  field('HSN', kit.hsn_code, ref.hsn_code);
  field('Min optional categories', kit.min_optional_count || 0, ref.min_optional_count || 0);

  const refCats = new Map((ref.categories || []).map((c) => [categoryKeyOf(c), c]));
  const kitCats = new Map((kit.categories || []).map((c) => [categoryKeyOf(c), c]));
  kitCats.forEach((c, key) => {
    const r = refCats.get(key);
    if (!r) {
      out.push({ type: 'add', text: `Category ${categoryNameOf(c)} (${(c.skus || []).length} SKUs)` });
      return;
    }
    if (c.min_qty !== r.min_qty || c.max_qty !== r.max_qty) {
      out.push({ type: 'change', text: `${categoryNameOf(c)} qty ${c.min_qty}-${c.max_qty} (other hubs ${r.min_qty}-${r.max_qty})` });
    }
    if (!!c.is_mandatory !== !!r.is_mandatory) {
      out.push({ type: 'change', text: `${categoryNameOf(c)} is ${c.is_mandatory ? 'mandatory' : 'optional'} (other hubs ${r.is_mandatory ? 'mandatory' : 'optional'})` });
    }
    const refSkus = new Set((r.skus || []).map((s) => s.product_id));
    const kitSkus = new Set((c.skus || []).map((s) => s.product_id));
    (c.skus || []).filter((s) => !refSkus.has(s.product_id)).forEach((s) => out.push({ type: 'add', text: `${skuNameOf(s)} in ${categoryNameOf(c)}` }));
    (r.skus || []).filter((s) => !kitSkus.has(s.product_id)).forEach((s) => out.push({ type: 'remove', text: `${skuNameOf(s)} in ${categoryNameOf(c)}` }));
  });
  refCats.forEach((r, key) => {
    if (!kitCats.has(key)) out.push({ type: 'remove', text: `Category ${categoryNameOf(r)}` });
  });
  return out;
};

// Config fields of a kit copy, in the shape updateKit / updateKitBulk expect
const configPayloadOf = (kit) => {
  const payload = {
    name: kit.name,
    discount_percent: Number(kit.discount_percent) || 0,
    min_optional_count: kit.min_optional_count || 0,
    categories: (kit.categories || []).map((c, index) => ({
      category_id: c.category_id,
      is_mandatory: c.is_mandatory,
      min_qty: c.min_qty,
      max_qty: c.max_qty,
      sort_order: index,
      label: c.label || null,
      skus: (c.skus || []).map((s) => ({ product_id: s.product_id })),
    })),
  };
  if (kit.gst_percent) payload.gst_percent = kit.gst_percent;
  if (kit.hsn_code) payload.hsn_code = kit.hsn_code;
  if (kit.variants && kit.variants.length) payload.variants = kit.variants;
  return payload;
};

const cellStyle = (state, selected) => {
  const base = {
    position: 'relative', width: '46px', height: '28px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    outline: selected ? '2px solid #0d6efd' : 'none', outlineOffset: '1px',
  };
  if (state === 'on') return { ...base, background: '#d1e7dd', color: '#0f5132', border: '1px solid #d1e7dd' };
  if (state === 'off') return { ...base, background: '#f1f3f5', color: '#868e96', border: '1px solid #f1f3f5' };
  return { ...base, background: 'white', color: '#adb5bd', border: '1px dashed #ced4da' };
};

const driftDot = { position: 'absolute', top: '-4px', right: '-4px', width: '9px', height: '9px', borderRadius: '50%', background: '#fd7e14', border: '2px solid white' };

const KitHubMatrix = ({ kits, warehouses, onReload }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(new Set());
  const [openCell, setOpenCell] = useState(null);
  const [discountValue, setDiscountValue] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);
  const [busy, setBusy] = useState(false);

  const hubs = useMemo(() => {
    const list = warehouses.map((wh) => ({
      key: String(wh.id),
      id: wh.id,
      name: wh.name,
      city: wh.city,
      isVirtual: wh.hub_type === 'VIRTUAL' || wh.hubType === 'VIRTUAL',
    }));
    if (kits.some((k) => !k.warehouse_id)) list.unshift({ key: ALL_HUBS_KEY, id: null, name: 'All hubs', city: '' });
    return list;
  }, [kits, warehouses]);

  const groups = useMemo(() => {
    const map = new Map();
    kits.forEach((kit) => {
      const key = groupKeyOf(kit);
      if (!map.has(key)) map.set(key, { key, batchId: kit.kit_batch_id || null, copies: {} });
      map.get(key).copies[hubKeyOf(kit)] = kit;
    });
    return Array.from(map.values()).map((g) => {
      const copies = Object.values(g.copies);
      // Reference copy = the configuration most hubs share
      const counts = {};
      copies.forEach((k) => { const s = signatureOf(k); counts[s] = (counts[s] || 0) + 1; });
      const majority = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
      const reference = copies.find((k) => signatureOf(k) === majority);
      const drift = {};
      if (copies.length > 1 && Object.keys(counts).length > 1) {
        Object.entries(g.copies).forEach(([hubKey, k]) => {
          if (signatureOf(k) !== majority) drift[hubKey] = diffKit(k, reference);
        });
      }
      const nameCounts = {};
      copies.forEach((k) => { nameCounts[k.name] = (nameCounts[k.name] || 0) + 1; });
      const name = Object.keys(nameCounts).sort((a, b) => nameCounts[b] - nameCounts[a])[0];
      return { ...g, name, reference, drift, liveCount: copies.filter((k) => k.active).length, copyCount: copies.length };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [kits]);

  const cellKey = (groupKey, hubKey) => `${groupKey}::${hubKey}`;
  const parseCell = (key) => {
    const [groupKey, hubKey] = key.split('::');
    const group = groups.find((g) => g.key === groupKey);
    return { group, hubKey, kit: group ? group.copies[hubKey] : null, hub: hubs.find((h) => h.key === hubKey) };
  };

  const toggleKeys = (keys) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = keys.every((k) => next.has(k));
      keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const clearSelection = () => { setSelected(new Set()); setOpenCell(null); setShowDiscount(false); };

  const handleCellClick = (group, hub) => {
    const key = cellKey(group.key, hub.key);
    toggleKeys([key]);
    setShowDiscount(false);
    setOpenCell(group.drift[hub.key] && !selected.has(key) ? key : null);
  };

  const selection = Array.from(selected).map(parseCell).filter((c) => c.group);
  const withKit = selection.filter((c) => c.kit);
  const toActivate = withKit.filter((c) => !c.kit.active);
  const toDeactivate = withKit.filter((c) => c.kit.active);
  const toAdd = selection.filter((c) => !c.kit && c.hub && c.hub.id && c.group.batchId);
  const selectedGroupKeys = new Set(selection.map((c) => c.group.key));

  const run = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      clearSelection();
      onReload();
    } catch (error) {
      toast.error(error.message || 'Update failed');
      onReload();
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = (cells, activate) => run(
    `${activate ? 'Activated' : 'Deactivated'} ${cells.length} hub ${cells.length === 1 ? 'copy' : 'copies'}`,
    async () => { for (const c of cells) await toggleKitStatus(c.kit.id); }
  );

  const handleAdd = () => {
    const byBatch = {};
    toAdd.forEach((c) => { (byBatch[c.group.batchId] = byBatch[c.group.batchId] || []).push(c.hub.id); });
    if (!window.confirm(`Add ${toAdd.length} kit ${toAdd.length === 1 ? 'copy' : 'copies'}? Each new hub gets the kit's current setup.`)) return;
    run(`Added the kit to ${toAdd.length} ${toAdd.length === 1 ? 'hub' : 'hubs'}`, async () => {
      for (const [batchId, ids] of Object.entries(byBatch)) await updateKitBulk(batchId, { warehouse_ids: ids });
    });
  };

  const handleDiscount = () => {
    const value = parseFloat(discountValue);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      toast.error('Enter a discount between 0 and 100');
      return;
    }
    run(`Discount set to ${value}% on ${withKit.length} hub ${withKit.length === 1 ? 'copy' : 'copies'}`, async () => {
      const byBatch = {};
      for (const c of withKit) {
        if (c.group.batchId && c.kit.warehouse_id) (byBatch[c.group.batchId] = byBatch[c.group.batchId] || []).push(c.kit.warehouse_id);
        else await updateKit(c.kit.id, { discount_percent: value });
      }
      for (const [batchId, ids] of Object.entries(byBatch)) await updateKitBulk(batchId, { discount_percent: value, warehouse_ids: ids });
    });
  };

  const handleEdit = () => {
    const first = withKit[0];
    const hubIds = selection.map((c) => c.hub && c.hub.id).filter(Boolean);
    const whParam = first.kit.warehouse_id ? `warehouseId=${first.kit.warehouse_id}` : 'fromWarehouse=all&warehouseId=all';
    navigate(`/kits/edit/${first.kit.id}?${whParam}&hubs=${hubIds.join(',')}`);
  };

  const handleMatchOthers = (group, hub, kit) => {
    if (!window.confirm(`Change the kit at ${hub.name} to match the other hubs? Its current differences will be lost.`)) return;
    run(`${hub.name} now matches the other hubs`, () => updateKit(kit.id, configPayloadOf(group.reference)));
  };

  const handleCopyToAll = (group, hub, kit) => {
    const others = Object.values(group.copies).filter((k) => k.id !== kit.id && k.warehouse_id);
    if (!window.confirm(`Copy the ${hub.name} setup to the other ${others.length} hubs of this kit? Their categories, SKUs and discount will be replaced.`)) return;
    run(`Copied the ${hub.name} setup to ${others.length} hubs`, () => updateKitBulk(group.batchId, {
      ...configPayloadOf(kit),
      warehouse_ids: Object.values(group.copies).map((k) => k.warehouse_id).filter(Boolean),
    }));
  };

  const open = openCell ? parseCell(openCell) : null;
  const diffColor = { add: '#198754', remove: '#dc3545', change: '#495057' };
  const diffSign = { add: '+ ', remove: '− ', change: '≠ ' };
  const btn = { fontSize: '12px', padding: '5px 12px' };

  return (
    <div>
      <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px', color: '#6c757d', marginBottom: '12px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ ...cellStyle('on'), width: '34px', height: '20px', cursor: 'default' }}>1%</span>Live (discount)</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ ...cellStyle('off'), width: '34px', height: '20px', cursor: 'default' }}>off</span>Switched off</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ ...cellStyle('none'), width: '34px', height: '20px', cursor: 'default' }}>+</span>Not added</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><span style={{ ...cellStyle('on'), width: '34px', height: '20px', cursor: 'default' }}>1%<span style={driftDot} /></span>Differs from other hubs</span>
        <span>Click cells, a hub name or a kit name to select.</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '13px', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 1, padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #dee2e6', minWidth: '220px' }}>Kit</th>
              {hubs.map((hub) => (
                <th
                  key={hub.key}
                  onClick={() => toggleKeys(groups.map((g) => cellKey(g.key, hub.key)))}
                  title={`${hub.name}${hub.city ? ` (${hub.city})` : ''}${hub.isVirtual ? ' · Virtual Hub' : ''}. Click to select this column.`}
                  style={{ padding: '8px 4px', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center' }}
                >
                  {hub.name}{hub.isVirtual ? ' (V)' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.key}>
                <td
                  onClick={() => toggleKeys(hubs.map((h) => cellKey(group.key, h.key)))}
                  title="Click to select this kit at every hub"
                  style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1, padding: '8px 12px', borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
                >
                  <div style={{ fontWeight: 600 }}>{group.name}</div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    {group.liveCount} of {group.copyCount} hub copies live
                    {Object.keys(group.drift).length > 0 && <span style={{ color: '#c35a00' }}> · {Object.keys(group.drift).length} differ</span>}
                  </div>
                </td>
                {hubs.map((hub) => {
                  const kit = group.copies[hub.key];
                  const state = kit ? (kit.active ? 'on' : 'off') : 'none';
                  const key = cellKey(group.key, hub.key);
                  return (
                    <td key={hub.key} style={{ padding: '6px 4px', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleCellClick(group, hub)}
                        style={cellStyle(state, selected.has(key))}
                        aria-label={`${group.name} at ${hub.name}`}
                        title={kit ? `${hub.name}: ${kit.active ? 'live' : 'switched off'}, discount ${kit.discount_percent}%${group.drift[hub.key] ? ', differs from other hubs' : ''}` : `${hub.name}: not added`}
                      >
                        {state === 'none' ? '+' : state === 'off' ? 'off' : `${Number(kit.discount_percent) || 0}%`}
                        {group.drift[hub.key] && <span style={driftDot} />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && open.kit && open.group.drift[open.hubKey] && (
        <div style={{ marginTop: '14px', border: '1px solid #ffe8cc', background: '#fff9f2', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>
            {open.group.name} at {open.hub.name}: differs from the other hubs
          </div>
          <ul style={{ margin: '0 0 12px', paddingLeft: '18px', fontSize: '13px' }}>
            {open.group.drift[open.hubKey].map((d, i) => (
              <li key={i} style={{ color: diffColor[d.type] }}>{diffSign[d.type]}{d.text}</li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" style={btn} disabled={busy} onClick={() => handleMatchOthers(open.group, open.hub, open.kit)}>Match the other hubs</button>
            {open.group.batchId && (
              <button type="button" className="btn btn-warning" style={btn} disabled={busy} onClick={() => handleCopyToAll(open.group, open.hub, open.kit)}>Copy this setup to all hubs</button>
            )}
            <button type="button" className="btn btn-secondary" style={btn} onClick={() => setOpenCell(null)}>Keep as is</button>
          </div>
        </div>
      )}

      {selection.length > 0 && (
        <div style={{ position: 'sticky', bottom: '12px', marginTop: '14px', background: '#e7f1ff', border: '1px solid #b6d4fe', borderRadius: '8px', padding: '10px 14px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', fontSize: '13px' }}>
          <strong style={{ marginRight: 'auto' }}>
            {selection.length} {selection.length === 1 ? 'cell' : 'cells'} selected
            {busy && <span style={{ fontWeight: 400, color: '#6c757d' }}> · saving…</span>}
          </strong>
          {showDiscount ? (
            <>
              <input
                type="number"
                className="form-control"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder="Discount %"
                min="0"
                max="100"
                step="0.5"
                style={{ width: '120px' }}
              />
              <button type="button" className="btn btn-primary" style={btn} disabled={busy || !withKit.length} onClick={handleDiscount}>Apply to {withKit.length}</button>
              <button type="button" className="btn btn-secondary" style={btn} onClick={() => setShowDiscount(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-success" style={btn} disabled={busy || !toActivate.length} onClick={() => handleToggle(toActivate, true)}>Activate{toActivate.length ? ` (${toActivate.length})` : ''}</button>
              <button type="button" className="btn btn-danger" style={btn} disabled={busy || !toDeactivate.length} onClick={() => handleToggle(toDeactivate, false)}>Deactivate{toDeactivate.length ? ` (${toDeactivate.length})` : ''}</button>
              <button type="button" className="btn btn-secondary" style={btn} disabled={busy || !toAdd.length} onClick={handleAdd}>Add to hub{toAdd.length ? ` (${toAdd.length})` : ''}</button>
              <button type="button" className="btn btn-secondary" style={btn} disabled={busy || !withKit.length} onClick={() => setShowDiscount(true)}>Set discount…</button>
              <button
                type="button"
                className="btn btn-warning"
                style={btn}
                disabled={busy || selectedGroupKeys.size !== 1 || !withKit.length}
                title={selectedGroupKeys.size !== 1 ? 'Select cells of one kit to edit it' : ''}
                onClick={handleEdit}
              >
                Edit kit for these hubs
              </button>
              <button type="button" className="btn btn-link" style={btn} onClick={clearSelection}>Clear</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default KitHubMatrix;
